import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropzone } from '../Dropzone';
import { DialogProps } from './DialogProps';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { IApplicationNotes } from '../../Interfaces/Application/IApplicationNotes';
import { AppState } from '../../store/rootReducer';
import { ApplicationDispatch } from '../../store/application/types';
import { Document, useApplicationDocumentTypes } from '../../Data/ApplicationDocumentData';
import { RequestDocuments } from '../Shared/RequestDocuments';
import {hasValue} from "@amsanalytics/react-shared-library";
import { ApplicationWorkflowDocument } from '../ApplicationWorkflowDocument';

// Shape of data this dialog will send to the caller.
export interface DocumentsAndNotesDialogResults {
  documents?: Document[];
  notes?: IApplicationNotes[];
}

export interface DocumentsAndNotesDialogProps extends DialogProps<DocumentsAndNotesDialogResults> {
  applicationId: string;
  requestDocuments?: boolean;
  requestNotes?: boolean;
  requestNotesPrompt?: string;
}

export default function DocumentsAndNotesDialog(props: DocumentsAndNotesDialogProps) {
  const employees = useSelector((state: AppState) => state.applications.employees);
  // List of notes added
  const [notes, setNotes] = useState<Partial<IApplicationNotes>[]>([]);
  // List of documents added
  const [documents, setDocuments] = useState<Partial<Document>[]>([]);
  // Current note being typed.
  const [currentNoteText, setcurrentNoteText] = useState<string>('');
  const dispatch = useDispatch<ApplicationDispatch>();
  // Various document types.
  const applicationDocumentTypes = useApplicationDocumentTypes(dispatch);
  // Current user
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  // When user deletes a document from the list.
  const onDelete = (document: Document): void => {
    // Filter out deleted document from list.
    const newDocuments = documents.filter((d) => d.name !== document.name);
    setDocuments(newDocuments);
  };

  const onDropAccepted = (files: File[]): void => {
    files.forEach((f) => {
      const nameWithoutExtension = f.name.substr(0, f.name.lastIndexOf('.')) || f.name;
      // Check if same file already exists.
      const existingDocument = documents?.find((d) => d.name === nameWithoutExtension && d.mimeType === f.type);
      if (existingDocument) {
        existingDocument.error = `This file has already been added.`;
      } else {
        // Otherwise, add new document to list.
        setDocuments([
          ...documents,
          {
            name: f.name,
            mimeType: f.type,
            applicationId: props.applicationId,
            applicationDocumentTypeIds: [], // no type IDs
            file: f,
            notes: undefined,
            isNew: true,
          },
        ]);
      }
    });
  };

  // User has added a new note.
  const addNote = (currentNoteText: string): void => {
    // Create a new note object.
    const newNote = {
      applicationId: props.applicationId,
      employee: activeUser!,
      employeeId: activeUser!.id,
      createdDate: new Date(),
      modifiedDate: null,
      deleted: false,
      note: currentNoteText,
    };
    // Add new note to list.
    setNotes([...notes, newNote]);
    // Clear text input after adding note.
    setcurrentNoteText('');
  };

  return (
    <>
      <DialogTitle>Add Documents and Notes</DialogTitle>
      <DialogContent className='workflow-request-dialog'>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {props.requestNotes && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h3>Notes</h3>
              <List>
                {notes.map((note, key) => {
                  const n = note as IApplicationNotes;
                  const employee = employees.find(e => e.id === n.employeeId);
                  
                  return (
                    <React.Fragment key={key}>
                      <ListItem>
                        <ListItemText
                          primary={new Date(n.createdDate).toLocaleString()}
                          secondary={
                            <React.Fragment>
                              <Typography component='span' variant='body2' style={{ display: 'inline' }} color='textPrimary'>
                                {employee?.firstName} {employee?.lastName}
                              </Typography>
                              — <span style={{ whiteSpace: 'pre-wrap' }}>{n.note}</span>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      <Divider variant='inset' component='li' />
                    </React.Fragment>
                  );
                })}
              </List>
              <TextField
                margin='dense'
                id='note-text'
                label={props.requestNotesPrompt ?? 'Enter a note...'}
                value={currentNoteText}
                onChange={(e) => setcurrentNoteText(e.currentTarget.value)}
                fullWidth
                multiline
                InputProps={{
                  endAdornment: (
                    <Button
                      color='primary'
                      disabled={!hasValue(currentNoteText)}
                      onClick={() => {
                        addNote( !props.requestNotesPrompt ? currentNoteText : props.requestNotesPrompt + ': ' + currentNoteText);
                      }}
                    >
                      Add
                    </Button>
                  ),
                }}
              />
            </div>
          )}
          {/* If both documents and notes requested, add space between both elements */}
          {props.requestNotes && props.requestDocuments && <div style={{ margin: '2em 0' }}></div>}
          {props.requestDocuments && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3>Documents</h3>
                <RequestDocuments />
              </div>
              <div
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                }}
              >
                <Dropzone disabled={false} onDropAccepted={(acceptedFiles) => onDropAccepted(acceptedFiles)} />

                <div
                  style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                  }}
                >
                  <List>
                    {applicationDocumentTypes &&
                      documents?.map((d, index) => (
                        <React.Fragment key={index}>
                          <ApplicationWorkflowDocument document={d as Document} onDelete={() => onDelete(d as Document)} />
                          <Divider variant='inset' component='li' />
                        </React.Fragment>
                      ))}
                  </List>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={props.closeDialog}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            props.accept && props.accept({ documents: documents as Document[], notes: notes as IApplicationNotes[]});
          }}
          color='primary'
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
}
